import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import './VotingComponent.css'; // Import the CSS file for styling
import LocationAndSchedule from './LocationAndSchedule'; // Import the new component
import { sessionTimeout, countEvents, minEvents } from './settings'; // Import settings
import { streakScheduleFires, removeLastVoteEmoji } from './streakLogic';
import { fetchFeed, postVote, STORAGE_KEYS, getContextId } from './v2Api';

const VotingComponent = ({ user, onAnonymousVote, embargoConfig, streakConfig }) => {

  const [events, setEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(null); // Set to null initially
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false); // Track if a fetch request is in progress
  const [authChecked, setAuthChecked] = useState(false); // Track if user authentication status has been checked
  // WEB-EXHAUSTED-DERIVE-1: these two are FACTS, not a verdict. "The deck is exhausted" is
  // derived from them (and from the deck itself) at render time — see `nothingLeftToLookAt`
  // below — so it can never depend on which of two async callbacks happened to land last.
  const [feedEmpty, setFeedEmpty] = useState(false); // Backend's last answer carried no options
  const [votedUids, setVotedUids] = useState(() => new Set()); // uids voted on in this session
  const [latestVotes, setLatestVotes] = useState(''); // Track latest votes as emojis
  const [lastVoteTime, setLastVoteTime] = useState(null);
  const [navigateStreak, setNavigateStreak] = useState(false); // State to trigger streak navigation
  const [fetchError, setFetchError] = useState(null); // Non-null when the event feed failed to load
  const [voteError, setVoteError] = useState(null); // Transient toast when a vote fails to record
  const [imageIndex, setImageIndex] = useState(0);
  const [failedImageUrls, setFailedImageUrls] = useState(new Set());
  const initializing = useRef(true); // Ref to track initialization status

  const navigate = useNavigate();
  const { event_id } = useParams();

  const logVotingState = (event_name) => {
    console.log(`\n--- ${event_name} ---`);
    console.log(`\tPrevious votes: ${latestVotes}`);
    console.log(`\tCurrent index: ${currentIndex}`);
    console.log(`\tRemaining events: ${events.length - (currentIndex || 0)}`);
    console.log(`\tTotal events: ${events.length}`);
    console.log(`\tEvents: ${events.map(event => event.title).join(', ')}`);
    console.log(`\tFetching: ${fetching}`);
    console.log(`\tLoading: ${loading}`);
    console.log(`\tAuth checked: ${authChecked}`);
    console.log(`\tLast vote time: ${lastVoteTime}`);
    console.log(`\tUser: ${user ? '[signed in]' : 'None'}`);
  };

  const readFromLocalStorage = () => {
    const storedEvents = localStorage.getItem(STORAGE_KEYS.unreviewedEvents);
    const storedLatestVotes = localStorage.getItem(STORAGE_KEYS.latestVotes) || '';
    const storedLastVoteTime = localStorage.getItem(STORAGE_KEYS.lastVoteTime) ? new Date(localStorage.getItem(STORAGE_KEYS.lastVoteTime)) : null;
    const storedCurrentIndex = parseInt(localStorage.getItem(STORAGE_KEYS.currentIndex), 10) || 0;

    console.log('Read from localStorage:', {
      storedEvents,
      storedLatestVotes,
      storedLastVoteTime,
      storedCurrentIndex
    });

    if (storedEvents && storedEvents !== '[]') {
      try {
        const parsedEvents = JSON.parse(storedEvents);
        const currentCtxId = getContextId();
        if (
          parsedEvents.length > 0 &&
          parsedEvents[0]._contextId &&
          parsedEvents[0]._contextId !== currentCtxId
        ) {
          // Cached deck belongs to a different context — discard it so postInitialization()
          // will call fetchEvents() with the correct context_id.
          localStorage.removeItem(STORAGE_KEYS.unreviewedEvents);
        } else {
          setEvents(parsedEvents);
          setLoading(false);
        }
      } catch (e) {
        console.warn('VotingComponent: corrupt unreviewedEvents in localStorage — clearing', e);
        localStorage.removeItem(STORAGE_KEYS.unreviewedEvents);
      }
    }
    setLatestVotes(storedLatestVotes);
    setLastVoteTime(storedLastVoteTime);

    console.log('Read from localStorage - setCurrentIndex', storedCurrentIndex);
    setCurrentIndex(storedCurrentIndex);
  };

  const resetLocalStorage = () => {
    localStorage.removeItem(STORAGE_KEYS.unreviewedEvents);
    localStorage.removeItem(STORAGE_KEYS.latestVotes);
    localStorage.removeItem(STORAGE_KEYS.lastVoteTime);
    localStorage.removeItem(STORAGE_KEYS.currentIndex);
  };

  useEffect(() => {
    if (lastVoteTime) {
      console.log('Setting lastVoteTime in localStorage:', {
        oldValue: localStorage.getItem(STORAGE_KEYS.lastVoteTime),
        newValue: lastVoteTime.toISOString()
      });
      localStorage.setItem(STORAGE_KEYS.lastVoteTime, lastVoteTime.toISOString());
    }
  }, [lastVoteTime]);

  useEffect(() => {
    if (latestVotes) {
      console.log('Setting latestVotes in localStorage:', {
        oldValue: localStorage.getItem(STORAGE_KEYS.latestVotes),
        newValue: latestVotes
      });
      localStorage.setItem(STORAGE_KEYS.latestVotes, latestVotes);
    }
  }, [latestVotes]);

  useEffect(() => {
    if (currentIndex !== null) {
      console.log('Setting currentIndex in localStorage:', {
        oldValue: localStorage.getItem(STORAGE_KEYS.currentIndex),
        newValue: currentIndex
      });
      localStorage.setItem(STORAGE_KEYS.currentIndex, currentIndex);
    }
  }, [currentIndex]);

  useEffect(() => {
    if (events.length > 0) {
      console.log('Setting unreviewedEvents in localStorage:', {
        oldValue: localStorage.getItem(STORAGE_KEYS.unreviewedEvents),
        newValue: JSON.stringify(events)
      });
      localStorage.setItem(STORAGE_KEYS.unreviewedEvents, JSON.stringify(events));
    }
  }, [events]);

  useEffect(() => {
    const initialize = async () => {
      if (user) {
        readFromLocalStorage();
        setAuthChecked(true); // User authentication check completed
        // loading stays true until fetchEvents completes (via finally block) when no cache hit
        initializing.current = false; // Set initializing to false after initialization

        // Navigate to the first event if no event_id is provided in the URL
        if (events.length > 0 && !event_id) {
          console.log('initialize - Navigating to first event:', { eventId: events[0].uid });
          navigate(`/options/${events[0].uid}`, { replace: true });
        }
      }
    };

    if (user !== undefined) {
      initialize();
    }
  }, [user]);

  useEffect(() => {
    const postInitialization = async () => {
      if (authChecked && !initializing.current) {
        // Clean session if expired
        if (lastVoteTime && (new Date() - lastVoteTime) / (1000 * 60) > sessionTimeout) {
          setLatestVotes('');
          resetLocalStorage();
        }

        // If session is empty, remember when session started
        if (!lastVoteTime) {
          const now = new Date();
          setLastVoteTime(now);
        }

        if (events.length > 0) {
          if (event_id) {
            const storedEvent = events.find(event => event.uid === event_id);
            if (storedEvent) {
              console.log('postInitialization - Setting currentIndex:', { newIndex: events.indexOf(storedEvent) });
              setCurrentIndex(events.indexOf(storedEvent));
              setLoading(false);
            } else {
              resetLocalStorage();
              fetchEvents(user, event_id);
            }
          } else {
            logVotingState('Loaded Events from Local Storage');
            // URL has no id — push the current card's uid into the address bar.
            // replace: true so /options doesn't sit in the back-stack.
            // WEB-OPTIONS-STALE-INDEX-1: `currentIndex` is restored from localStorage even when the
            // cached deck it indexed was discarded for belonging to another context (see
            // readFromLocalStorage above — it clears `unreviewedEvents`, not `currentIndex`). A stale
            // index past the end of the fresh deck used to yield `undefined` here: no navigate fired,
            // the URL stayed bare `/options` with a NON-EMPTY feed (a real violation of the URL
            // invariant, unlike the empty-deck exception), and the render guard `!events[currentIndex]`
            // showed "Loading..." forever. Clamping repairs both: the navigate lands on a real uid, and
            // the resulting `event_id` branch above resets `currentIndex` to that card.
            const safeIndex = Math.min(currentIndex ?? 0, events.length - 1);
            const targetUid = events[safeIndex]?.uid;
            if (targetUid) navigate(`/options/${targetUid}`, { replace: true });
          }
        } else {
          fetchEvents(user, event_id);
        }
      }
    };

    if (authChecked) {
      postInitialization();
    }
  }, [authChecked, events, event_id, lastVoteTime, user]);

  const fetchEvents = async (user, firstUid = null) => {
    if (fetching) return; // Prevent simultaneous fetch requests
    setFetching(true);
    setFetchError(null);

    try {
      const cards = await fetchFeed(user, { count: countEvents, firstUid });
      // WEB-EXHAUSTED-DERIVE-1: record only what the backend actually said — "it had nothing
      // to give". That is NOT the same fact as "the user has nothing left to look at": the
      // client can still be holding up to `minEvents` undelivered cards (the refetch trigger
      // fires while they are in hand), and the backend can go empty because those same options
      // were voted on another device. Deciding exhaustion here is what made the flag depend on
      // whether this response landed before or after postInitialization — see BUGS.md BUG-WEB.10.
      setFeedEmpty(cards.length === 0);
      if (cards.length > 0) {
        // BUG-WEB.10 (A): the backend has no concept of "already delivered" — it only
        // excludes options already in votes_v2. Any card the client still holds (the
        // unvoted tail when the refetch trigger fires, the deep-link refetch that clears
        // localStorage but not `events`, or a refetch racing an unsynced vote) can come
        // back in the fresh draw. Appending it a second time puts a duplicate uid in the
        // deck, and the user is walked through the same option again — a replay loop.
        // Drop any uid the client already holds before appending.
        const existingUids = new Set(events.map(e => e.uid));
        const newCards = cards.filter(c => !existingUids.has(c.uid));
        const updatedEvents = [...events, ...newCards];
        setEvents(updatedEvents);
      }
      logVotingState('Fetched New Events (v2)');
    } catch (error) {
      console.error('Error fetching v2 feed:', error);
      setFetchError('Could not load events — check your connection.');
    } finally {
      setFetching(false);
      setLoading(false);
    }
  };

  const handleVote = async (vote) => {
    if (!user || events.length === 0) return;

    const currentEvent = events[currentIndex];

    // WEB-EXHAUSTED-DERIVE-1: the deck's own record of what the user has been through.
    // `exhausted` is derived from this, so it stays correct no matter when the feed answers.
    setVotedUids(prev => {
      const next = new Set(prev);
      next.add(currentEvent.uid);
      return next;
    });

    const votePayload = {
      vote: vote
    };

    if (currentEvent.type === 'event') {
      votePayload.event_id = currentEvent.uid;
    } else if (currentEvent.type === 'art') {
      votePayload.art_id = currentEvent.uid;
    }

    // Show the next event immediately
    const newIndex = currentIndex + 1;

    // Trigger fetchEvents before running out of events.
    //
    // BUG-WEB.10: this fires before the `postVote` at the end of this function, so the
    // backend's votes_v2 read can miss the vote just cast and hand the option straight back.
    // The dedup in fetchEvents above makes that harmless, which is why this ordering is left
    // alone. Moving postVote above this trigger — the "fix B" the bug report proposed — no longer
    // breaks the end-of-deck screen: WEB-EXHAUSTED-DERIVE-1 removed the position-sensitivity that
    // made it regress, and wt6-vote-uid-route.spec.js:381 was measured green with B applied
    // (2026-08-05). B is still NOT landed, for two reasons measured in the same run: it makes the
    // user wait for the vote round-trip before the next card appears, and it eliminates cause (c)
    // outright — bugweb10-feed-dedup.spec.js's second test then fails its own guard, because the
    // backend can no longer be provoked into re-offering the option just voted on.
    if (newIndex >= events.length - minEvents) {
      fetchEvents(user);
    }

    if (newIndex >= events.length) {
      setLoading(true); // Show loader if there are no more events
    } else {
      setCurrentIndex(newIndex);
      navigate(`/options/${events[newIndex]?.uid}`);
    }

    // Update latest votes string
    let newLatestVotes = latestVotes;
    if (vote === 'up') newLatestVotes += '👍';
    if (vote === 'down') newLatestVotes += '👎';
    if (vote === 'favorite') newLatestVotes += '⭐';
    setLatestVotes(newLatestVotes);
    // Write synchronously so the value persists even if navigation unmounts this component
    // before the useEffect has a chance to fire.
    if (newLatestVotes) localStorage.setItem(STORAGE_KEYS.latestVotes, newLatestVotes);

    // Update last vote time
    const now = new Date();
    setLastVoteTime(now);

    const newVoteCount = [...newLatestVotes].length;
    localStorage.setItem(STORAGE_KEYS.voteCount, newVoteCount);
    // CFG-STREAK-WEB-1 / D-1: no config → no interrupt (same gate as the sign-in wall).
    if (streakScheduleFires(newVoteCount, streakConfig)) {
      setNavigateStreak(true);
    }

    // Sign-in wall: notify App.js after each anonymous vote so it can
    // track the threshold (up/favorite = positive).
    if (user?.isAnonymous && onAnonymousVote) {
      onAnonymousVote(vote === 'up' || vote === 'favorite');
    }

    logVotingState('User Voted');

    // Send the vote to the server
    try {
      await postVote(user, { optionId: currentEvent._optionId, legacyVote: vote });
    } catch (error) {
      console.error('Error recording vote:', error);
      setVoteError('Pick not saved — check your connection.');
      setTimeout(() => setVoteError(null), 4000);
    }
  };

  useEffect(() => {
    if (navigateStreak) {
      navigate('/streak');
    }
  }, [navigateStreak, navigate]);

  useEffect(() => {
    setImageIndex(0);
    setFailedImageUrls(new Set());
  }, [currentIndex]);

  const handlePreviousEvent = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      console.log('handlePreviousEvent - Setting currentIndex to newIndex:', { newIndex });
      setCurrentIndex(newIndex);
      const newLatestVotes = removeLastVoteEmoji(latestVotes); // Remove the last emoji from the streak
      setLatestVotes(newLatestVotes);
      console.log('handlePreviousEvent - Navigating to event:', { eventId: events[newIndex].uid, currentUrlEventId: event_id });
      navigate(`/options/${events[newIndex].uid}`);

      logVotingState('Went to Previous Event');
    }
  };

  if (!authChecked || currentIndex === null) {
    return <div>Loading...</div>;
  }

  // WEB-EXHAUSTED-DERIVE-1 — "you've reviewed everything" is a statement about the deck, not
  // about when a fetch happened to answer. All three have to hold:
  //   1. the cursor is on the last card the client holds AND that card has been voted, so
  //      there is physically nothing further to advance to (cards the user walked BACK to are
  //      still something to look at — that is why this is a cursor test, not "all uids voted");
  //   2. the backend's last answer carried no options, so it has nothing more to give;
  //   3. no refetch is outstanding that could still bring some.
  const atEndOfDeck = events.length > 0 && currentIndex >= events.length - 1;
  const currentCardVoted = votedUids.has(events[currentIndex]?.uid);
  const nothingLeftToLookAt = atEndOfDeck && currentCardVoted;
  const exhausted = feedEmpty && !fetching && nothingLeftToLookAt;

  if (exhausted) {
    return (
      <div className="VotingComponent" data-testid="deck-exhausted">
        <h2>You've reviewed everything!</h2>
        <p>You've seen all available events. Check your picks or come back when more are added.</p>
        <div className="vote-buttons">
          <button onClick={() => navigate('/picks')} className="active-button">📋 View my picks</button>
        </div>
      </div>
    );
  }

  if (fetchError && events.length === 0) {
    return (
      <div className="VotingComponent" data-testid="deck-fetch-error">
        <p style={{ color: '#ff3b30', marginBottom: 12 }}>{fetchError}</p>
        <button className="active-button" onClick={() => fetchEvents(user)}>Try again</button>
      </div>
    );
  }

  if (events.length === 0 && !loading) {
    return <div className="VotingComponent" data-testid="deck-no-events">No events yet — check back soon!</div>;
  }

  if (loading || !events[currentIndex]) {
    return <div>Loading...</div>;
  }

  const currentEvent = events[currentIndex];
  const previousEventTitle = currentIndex > 0 ? events[currentIndex - 1]?.title : null;

  const visibleImages = (currentEvent.images ?? []).filter(
    img => !failedImageUrls.has(img.thumbnail_url)
  );
  const effectiveIndex = Math.min(imageIndex, Math.max(0, visibleImages.length - 1));

  // AUD-PAR-7: event-type badge from taxonomy stored by App.js on mount.
  // Uses tagType (the first non-'art' tag from the raw option) for taxonomy lookup,
  // not type ('event'|'art') which is the semantic discriminator used for scheduling.
  // CR-6: type was changed from tag string to 'event'|'art'; tagType preserves the tag.
  const taxonomyRaw = localStorage.getItem(STORAGE_KEYS.v2Taxonomy);
  let taxonomy = null;
  if (taxonomyRaw) {
    try {
      taxonomy = JSON.parse(taxonomyRaw);
    } catch (e) {
      console.warn('VotingComponent: corrupt v2.taxonomy in localStorage — clearing', e);
      localStorage.removeItem(STORAGE_KEYS.v2Taxonomy);
    }
  }
  const eventTypeLabel = taxonomy?.tags?.[currentEvent.tagType];

  return (
    <div className="VotingComponent">
      {voteError && (
        <div style={{
          background: '#2a1010', border: '1px solid #ff3b30', borderRadius: 8,
          color: '#ff3b30', padding: '10px 14px', marginBottom: 12, fontSize: 13,
        }} data-testid="vote-error-toast">
          {voteError}
        </div>
      )}
      {previousEventTitle && (
        <div className="previous-event">
          <a href="#" onClick={handlePreviousEvent}>&lt;&lt; {previousEventTitle}</a>
        </div>
      )}
      {eventTypeLabel && (
        <span className="event-type-badge" data-testid="event-type-badge">{eventTypeLabel}</span>
      )}
      <h1>{currentEvent.title}</h1>
      {visibleImages.length > 0 && (
        <>
          <div className="image-gallery">
            <img
              src={visibleImages[effectiveIndex].thumbnail_url}
              alt={currentEvent.title}
              data-testid="gallery-image"
              onError={() => {
                const url = visibleImages[effectiveIndex]?.thumbnail_url;
                if (url) setFailedImageUrls(s => { const n = new Set(s); n.add(url); return n; });
              }}
            />
            {visibleImages.length > 1 && (
              <>
                <button
                  className="gallery-prev"
                  onClick={() => setImageIndex(i => Math.max(0, i - 1))}
                  disabled={effectiveIndex === 0}
                  aria-label="Previous image"
                >‹</button>
                <button
                  className="gallery-next"
                  onClick={() => setImageIndex(i => Math.min(visibleImages.length - 1, i + 1))}
                  disabled={effectiveIndex === visibleImages.length - 1}
                  aria-label="Next image"
                >›</button>
              </>
            )}
          </div>
          {visibleImages.length > 1 && (
            <div className="gallery-dots">
              {visibleImages.map((_, i) => (
                <span key={i} className={`dot${i === effectiveIndex ? ' active' : ''}`} />
              ))}
            </div>
          )}
        </>
      )}
      <p>{currentEvent.description}</p>

      {currentEvent.type === 'event' && <LocationAndSchedule event={currentEvent} embargoConfig={embargoConfig} />}

      <div className="vote-buttons">
        <button onClick={() => handleVote('down')} className="active-button" data-testid="vote-reject">👎 Not for me</button>
        <button onClick={() => handleVote('up')} className="active-button" data-testid="vote-maybe">👍 Maybe</button>
        <button onClick={() => handleVote('favorite')} className="active-button" data-testid="vote-favorite">⭐ Exciting!</button>
      </div>
      {latestVotes && <div className="latest-votes">Your streak: {latestVotes}</div>}

      {/* PromoBlock hidden pending UX review — see BACKLOG.md */}
    </div>
  );
};

export default VotingComponent;
